/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type SaUsage } from '../../lib/api/sa.js';
import { useSaUsage } from '../../lib/queries/sa.js';

const HEALTH_TONE: Readonly<Record<string, BadgeTone>> = {
  green: 'green',
  amber: 'amber',
  red: 'rose',
  unknown: 'slate',
};

const COLS = [
  'clinic',
  'health',
  'sessions',
  'whatsapp',
  'sms',
  'pdfs',
  'storage',
  'users',
] as const;

function UsageRow({ row }: { readonly row: SaUsage }): JSX.Element {
  const { t } = useTranslation();
  return (
    <tr className="border-b border-slate-100 last:border-0 dark:border-slate-800">
      <td className="py-3 pr-4 font-medium text-slate-800 dark:text-slate-100">{row.clinicName}</td>
      <td className="py-3 pr-4">
        <Badge
          label={t(`sa.usage.health.${row.health}`, row.health)}
          tone={HEALTH_TONE[row.health] ?? 'slate'}
        />
      </td>
      <td className="py-3 pr-4 text-slate-700 dark:text-slate-200">{row.sessions30d}</td>
      <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{row.whatsapp30d}</td>
      <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{row.sms30d}</td>
      <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{row.pdfs30d}</td>
      <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{row.storageMb} MB</td>
      <td className="py-3 text-slate-600 dark:text-slate-300">{row.activeUsers}</td>
    </tr>
  );
}

/** Super Admin → Usage & Health. Per-clinic 30-day usage + RAG health (read-only). */
export function SaUsagePage(): JSX.Element {
  const { t } = useTranslation();
  const query = useSaUsage();
  const rows = query.data ?? [];
  const counts: Record<string, number> = {};
  for (const r of rows) {
    counts[r.health] = (counts[r.health] ?? 0) + 1;
  }

  return (
    <div className="space-y-6 p-8">
      <PageHeader
        title={t('sa.usage.title')}
        subtitle={t('sa.usage.healthSummary', {
          green: counts.green ?? 0,
          amber: counts.amber ?? 0,
          red: counts.red ?? 0,
        })}
      />
      <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
        <QueryState
          isLoading={query.isLoading}
          isError={query.isError}
          isEmpty={rows.length === 0}
          loadingLabel={t('common.loading')}
          errorLabel={t('common.loadError')}
          emptyLabel={t('sa.usage.empty')}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400 dark:border-slate-700">
                  {COLS.map((col) => (
                    <th key={col} className="pb-2 pr-4 font-medium">
                      {t(`sa.usage.col.${col}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <UsageRow key={row.clinicId} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        </QueryState>
      </div>
    </div>
  );
}
